/**
 * Dev-only "Reset demo patient" button.
 *
 * Resets the synthetic demo patient (Alex Johnson, P-789) back to his
 * clean-slate name+DOB-only state via `POST /api/admin/demo-patient/reset`,
 * so a provider can start a fresh demo on demand without a redeploy. Renders
 * only when `config.ENVIRONMENT !== "production"` (mirroring the backend
 * route's own hard gate — a 404 in production, see `app.routers.admin`) AND
 * the currently open patient IS the synthetic demo patient. This is
 * deliberately narrow: it must never render, or be callable, for a real
 * patient or in production.
 */
import React, { useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import config from "../config";
import logger from "../utils/logger";

/** The synthetic demo patient this button is scoped to (Alex Johnson). */
export const DEMO_PATIENT_ID = "P-789";

interface DemoPatientResetButtonProps {
  /** The currently open patient id; the button only renders for the demo patient. */
  patientId: string;
  /** Called after a successful reset so the caller can refresh its views. */
  onReset?: () => void;
}

const DemoPatientResetButton: React.FC<DemoPatientResetButtonProps> = ({
  patientId,
  onReset,
}) => {
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEnabled =
    config.ENVIRONMENT !== "production" && patientId === DEMO_PATIENT_ID;
  if (!isEnabled) {
    return null;
  }

  const handleClick = async (): Promise<void> => {
    if (
      !window.confirm(
        "Reset Alex Johnson to a clean, empty demo patient? This clears all chat, ingested sources, and generated documents for him.",
      )
    ) {
      return;
    }
    setResetting(true);
    setError(null);
    try {
      const resp = await apiUtils.fetchData<{
        patient_id: string;
        status: string;
      }>("/api/admin/demo-patient/reset", "POST");
      if (isApiError(resp)) {
        setError("Reset failed. Please try again.");
        logger.devOnly.warn("DemoPatientResetButton: reset failed");
        return;
      }
      onReset?.();
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid="reset-demo-patient-button"
        onClick={() => void handleClick()}
        disabled={resetting}
        title="Dev only: reset the demo patient to a clean slate"
        className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {resetting ? "Resetting…" : "Reset demo patient"}
      </button>
      {error && (
        <span className="text-xs text-red-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default DemoPatientResetButton;
